import { useCallback, useMemo, useState } from "react";
import { formValueSelector, type TypedWrappedFieldProps } from "redux-form";
import _ from "underscore";

import {
  Box,
  Button,
  IconButton,
  Stack,
  Typography,
} from "@braintrust/braintrust-ui-components";
import {
  CloseIcon,
  EditPenIcon,
  ErrorSVGIcon,
  InfoIcon,
} from "@braintrust/braintrust-ui-components/Icons";
import { openAILoadingModal } from "@js/apps/common/ai-loading-modal";
import { Snackbar } from "@js/components/snackbar";
import { SortableList } from "@js/components/sortable-list/motion";
import { useLatestCallback } from "@js/hooks/use-latest-callback";
import { store } from "@js/store";
import type {
  JobFormInterviewGradingCriteria,
  JobFormValues,
} from "@js/types/jobs";

import { useGenerateGradingCriteriaMutation } from "../../api";
import { INTERVIEW_QUESTIONS_FORM_ID } from "../../constants";
import { MAX_CRITERIA_ITEMS_COUNT } from "../../validator";
import { AddItemTextField } from "../add-item-text-field";
import { AIInterviewInfoAlert } from "../ai-interview-info-alert";

import { JobInterviewGradingCriteriaItem } from "./job-interview-grading-criteria-item";

import styles from "./styles.module.scss";

const interviewFormValuesSelector = formValueSelector(
  INTERVIEW_QUESTIONS_FORM_ID,
);

type JobInterviewGradingCriteriaProps = TypedWrappedFieldProps<
  JobFormInterviewGradingCriteria[]
> & {
  jobId: number;
  questionsWereChanged: boolean;
  onRegenerateCriteria: () => void;
};

export const JobInterviewGradingCriteria = ({
  input,
  meta,
  jobId,
  questionsWereChanged,
  onRegenerateCriteria,
}: JobInterviewGradingCriteriaProps) => {
  const [generateGradingCriteria, { isLoading: isGenerating }] =
    useGenerateGradingCriteriaMutation();
  const [hasChanged, setHasChanged] = useState(false);
  const isErrorInSection = !!meta.error;
  const isAnyTouched = !!meta.dirty || meta.submitFailed;
  const displayError = isErrorInSection && isAnyTouched;
  const generalError = meta.error?._error
    ? String(meta.error?._error)
    : undefined;

  const validationError =
    typeof meta.error === "string" ? meta.error : undefined;

  const sectionError = generalError || validationError;

  const [criteriaItems, setCriteriaItems] = useState<
    JobFormInterviewGradingCriteria[]
  >(input.value || []);

  const updateItems = (items: JobFormInterviewGradingCriteria[]) => {
    setHasChanged(true);
    input.onChange(items);
    setCriteriaItems(items);
  };

  const updateItemsCallback = useLatestCallback(updateItems);
  const debounceReorder = useMemo(() => {
    return _.debounce(updateItemsCallback, 100);
  }, [updateItemsCallback]);

  const handleChangeItem = useLatestCallback(
    (id: number, patch: Partial<JobFormInterviewGradingCriteria>) => {
      updateItems(
        criteriaItems.map((criteria) =>
          criteria.id !== id ? criteria : { ...criteria, ...patch },
        ),
      );
    },
  );

  const handleDeleteItem = useLatestCallback((id: number) => {
    updateItems(
      criteriaItems
        .filter((criteria) => criteria.id !== id)
        .map((item, index) => {
          return { ...item, order: index };
        }),
    );
  });

  const getCurrentInterviewQuestions = useCallback(() => {
    const state = store.getState();
    const values = interviewFormValuesSelector(
      state,
      "interview_questions",
    ) as JobFormValues["interview_questions"];
    const formattedQuestions =
      values?.map((question) => question?.question) ?? [];

    return formattedQuestions;
  }, []);

  const handleUpdateCriteria = async () => {
    if (isGenerating || !questionsWereChanged) {
      return;
    }
    const { closeAILoadingModal } = openAILoadingModal({
      text: "We're generating your advanced scoring…",
    });

    try {
      const questions = getCurrentInterviewQuestions();
      const newCriteria = await generateGradingCriteria({
        jobId,
        questions,
      }).unwrap();

      const criteriasWithIds = newCriteria.map((criteria, index) => {
        return { ...criteria, id: index + 1, order: index };
      });
      updateItems(criteriasWithIds);
      onRegenerateCriteria();
    } catch (_error) {
      Snackbar.error("Something went wrong!");
    }

    closeAILoadingModal();
  };

  const showAddCriteriaButton = criteriaItems.length < MAX_CRITERIA_ITEMS_COUNT;

  return (
    <Stack
      sx={{
        padding: 4,
        borderRadius: 4,
        backgroundColor: "var(--white)",
        border: displayError
          ? "1px solid var(--negative-2)"
          : "1px solid var(--grey-4)",
      }}
    >
      {questionsWereChanged && (
        <QuestionsChangedAlert
          handleUpdateCriteria={handleUpdateCriteria}
          isGeneratingCriteria={isGenerating}
        />
      )}
      {hasChanged && (
        <CriteriaChangedAlert dismiss={() => setHasChanged(false)} />
      )}
      <Stack direction="row" sx={{ alignItems: "center", mb: 0.5 }}>
        {displayError && (
          <ErrorSVGIcon
            sx={{
              color: "var(--negative-2)",
              fontSize: "20px",
              mr: 1,
            }}
          />
        )}
        <Typography component="h1" size="large" fontWeight={400}>
          Evaluation Guide
        </Typography>
      </Stack>
      <Typography
        component="span"
        size="small"
        fontWeight={400}
        sx={{ mb: criteriaItems.length ? 3 : 0 }}
      >
        Edit the focus areas below to teach the AI how responses should be
        evaluated
      </Typography>
      <SortableList<JobFormInterviewGradingCriteria>
        items={criteriaItems}
        onReorder={async (
          reorderedItems: JobFormInterviewGradingCriteria[],
        ) => {
          const itemsWithOrder = reorderedItems.map((item, index) => ({
            ...item,
            order: index,
          }));
          debounceReorder(itemsWithOrder);
        }}
        className={styles.sortableList}
        style={{
          display: "grid",
          gap: "16px",
        }}
      >
        {(item, index, isDragging) => {
          return (
            <JobInterviewGradingCriteriaItem
              index={index}
              item={item}
              isDragging={isDragging}
              onDelete={handleDeleteItem}
              onChange={handleChangeItem}
              error={meta.error?.[index]}
              displayError={displayError}
              getCurrentInterviewQuestions={getCurrentInterviewQuestions}
              jobId={jobId}
            />
          );
        }}
      </SortableList>

      {showAddCriteriaButton && (
        <Box sx={{ mt: 2, with: "100%" }}>
          <AddItemTextField
            iconButtonAriaLabel="Add evaluation criteria"
            id="add-evaluation-criteria"
            placeholder="Add additional evaluation criteria"
            onAdd={(definition: string) => {
              const newItemId = !!criteriaItems.length
                ? Math.max(...(criteriaItems || []).map((item) => item.id)) + 1
                : 0;
              const newItemOrder = criteriaItems.length;
              updateItems([
                ...criteriaItems,
                getEmptyInterviewCriteriaItem({
                  definition,
                  newItemId,
                  order: newItemOrder,
                }),
              ]);
            }}
          />
        </Box>
      )}

      {displayError && !!sectionError && (
        <Box
          sx={{
            background: "#F7626240",
            padding: "8px 16px",
            borderRadius: "8px",
            mt: 2,
          }}
        >
          <Typography component="p" size="small">
            {sectionError}
          </Typography>
        </Box>
      )}
    </Stack>
  );
};

const getEmptyInterviewCriteriaItem = ({
  newItemId,
  order,
  definition,
}: {
  definition: string;
  newItemId: number;
  order: number;
}): JobFormInterviewGradingCriteria => {
  return {
    id: newItemId,
    definition,
    name: "",
    normalized_name: "",
    excellent: "",
    fair: "",
    good: "",
    poor: "",
    very_good: "",
    order,
    isNew: true,
  };
};

const CriteriaChangedAlert = ({ dismiss }: { dismiss: () => void }) => {
  return (
    <AIInterviewInfoAlert
      text={
        <Box>
          <InfoIcon sx={{ fontSize: "1rem", verticalAlign: "middle", mr: 1 }} />
          <Typography component="span" variant="paragraph" size="small">
            You've updated your Evaluation Guide. New interviews will use these
            definitions. Previous interviews will not be affected.
          </Typography>
        </Box>
      }
      action={
        <IconButton
          onClick={dismiss}
          variant="transparent"
          aria-label="Dismiss alert"
          sx={{ padding: "4px !important" }}
        >
          <CloseIcon />
        </IconButton>
      }
      sx={{ mb: 2 }}
    />
  );
};

const QuestionsChangedAlert = ({
  handleUpdateCriteria,
  isGeneratingCriteria,
}: {
  handleUpdateCriteria: () => void;
  isGeneratingCriteria: boolean;
}) => {
  return (
    <AIInterviewInfoAlert
      text={
        <Box>
          <EditPenIcon
            sx={{ fontSize: "1rem", verticalAlign: "middle", mr: 1 }}
          />
          <Typography component="span" variant="paragraph" size="small">
            Adjust your grading criteria to match the revised questions
          </Typography>
        </Box>
      }
      action={
        <Button
          onClick={handleUpdateCriteria}
          variant="secondary-thin"
          shape="squared"
          size="x-small"
          sx={{
            backgroundColor: "var(--white) !important",
            pointerEvents: isGeneratingCriteria ? "none" : undefined,
          }}
        >
          Update
        </Button>
      }
      sx={{ mb: 2 }}
    />
  );
};
